import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import '../../styles/AuthForm.css';
import API_BASE from '../../utils/api';
import notify from '../../utils/notify';

function Signup() {
  const [company, setCompany] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [optIn, setOptIn] = useState(false);
  const [inviteError, setInviteError] = useState('');
  const [inviteChecking, setInviteChecking] = useState(false);

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get('invite') || '';
  const invitedEmail = searchParams.get('email') || '';
  const planParam = searchParams.get('plan') || '';
  const billingParam = searchParams.get('billing') || '';

  useEffect(() => {
    if (invitedEmail) {
      setEmail(invitedEmail);
    }
  }, [invitedEmail]);

  // Carries the plan/billing choice from marketing site pricing links
  // (e.g. interiorfolio.com/plans) through signup + email confirmation,
  // so the in-app Plans page can pre-select it.
  useEffect(() => {
    if (planParam !== 'standard' && planParam !== 'premium') return;

    localStorage.setItem(
      'signupPlan',
      JSON.stringify({
        plan: planParam,
        billing: billingParam === 'annual' ? 'annual' : 'monthly',
      })
    );
  }, [planParam, billingParam]);

  useEffect(() => {
    if (!inviteToken) return;

    async function validateInvite() {
      setInviteChecking(true);
      setInviteError('');
      try {
        const res = await fetch(
          `${API_BASE}/api/auth/additional-user-invite/${encodeURIComponent(inviteToken)}`
        );
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          throw new Error(data?.error || 'Invitation is invalid');
        }
      } catch (err) {
        setInviteError(err.message || 'Invitation is invalid');
      } finally {
        setInviteChecking(false);
      }
    }

    validateInvite();
  }, [inviteToken]);

  const handleSignup = async (e) => {
    e.preventDefault();

    try {
      const res = await fetch(`${API_BASE}/api/auth/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          company,
          firstName,
          lastName,
          email,
          password,
          inviteToken: inviteToken || undefined,
          optIn,
        }),
      });

      const data = await res.json();
      if (res.ok) {
        notify('Signup successful! Please verify your email.');
        navigate('/confirm', { state: { email, inviteToken: inviteToken || null } });
      } else {
        notify(data.error || 'Signup failed');
      }
    } catch (err) {
      console.error(err);
      notify('Something went wrong.');
    }
  };

  return (
    <div className="auth-form">
      <img src="/logo.png" alt="InteriorFolio Logo" className="auth-logo" />

      <form className="auth-card signup-card" onSubmit={handleSignup}>
        <h2>Create Your Account</h2>
        {inviteChecking && <p>Checking your invitation...</p>}
        {inviteError && <p style={{ color: '#b91c1c' }}>{inviteError}</p>}

        <div className="signup-grid">
          {!inviteToken && (
            <div className="field-group signup-span-2">
              <label>Company Name <span className="required-mark">*</span></label>
              <input
                type="text"
                // placeholder=""
                value={company}
                onChange={(e) => setCompany(e.target.value)}
                required
              />
            </div>
          )}
          <div className="field-group">
            <label>First Name <span className="required-mark">*</span></label>
            <input
              type="text"
              // placeholder="First name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              required
            />
          </div>
          <div className="field-group">
            <label>Last Name <span className="required-mark">*</span></label>
            <input
              type="text"
              // placeholder="Last name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              required
            />
          </div>
          <div className="field-group signup-span-2">
            <label>Email <span className="required-mark">*</span></label>
            <input
              type="email"
              // placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              readOnly={Boolean(invitedEmail)}
              required
            />
          </div>
          <div className="field-group signup-span-2">
            <label>Password <span className="required-mark">*</span></label>
            <input
              type="password"
              // placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>
        </div>

        <div className="form-check">
          <input id="optIn" type="checkbox" checked={optIn} onChange={() => setOptIn(!optIn)} />
          <label htmlFor="optIn">
            Join our design community to receive updates on the latest news.
          </label>
        </div>

        <button type="submit" disabled={inviteChecking || Boolean(inviteError)}>
          Next
        </button>

        <p>
          Already have an account? <Link to="/">Log In</Link>
        </p>
      </form>
    </div>
  );
}

export default Signup;
